import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const ADMIN_KEY = "dc_admin_pw";
const CLUB_KEY = "dc_active_club";
// NOTE: Dies ist ein geteiltes Club-Passwort (kein persönlicher JWT-Token). Der User hat
// bewusst „keine Anmeldung" gewählt (nur ein Admin-Passwort für die Verwaltung).
// Ein XSS-Risiko besteht nur für ADMIN-Zugriff und ist im Trust-Modell akzeptiert.
export const getAdminPw = () => localStorage.getItem(ADMIN_KEY) || "";
export const setAdminPw = (v) => {
  localStorage.setItem(ADMIN_KEY, v);
  window.dispatchEvent(new Event("admin-auth-changed"));
};
export const clearAdminPw = () => {
  localStorage.removeItem(ADMIN_KEY);
  window.dispatchEvent(new Event("admin-auth-changed"));
};
export const isAdmin = () => !!getAdminPw();
export const getActiveClubId = () => localStorage.getItem(CLUB_KEY) || "";
export const setActiveClubId = (clubId) => localStorage.setItem(CLUB_KEY, clubId);
export const getActiveClubName = () => ({
  "dc-atenser-allee": "DC Atenser Allee",
  "dc-pfeile-statt-worte": "DC Pfeile statt Worte",
}[getActiveClubId()] || "Dartgruppe");
export const getActiveClubLogo = () => ({
  "dc-atenser-allee": "/dashboard/dcaa-logo.jpg",
  "dc-pfeile-statt-worte": "/dashboard/psw-logo.png",
}[getActiveClubId()] || null);

const adminHeaders = () => ({ "X-Admin-Password": getAdminPw() });

axios.interceptors.request.use((config) => {
  const clubId = getActiveClubId();
  const url = String(config.url || "");
  if (clubId && url.startsWith(API) && !url.endsWith("/clubs") && !url.endsWith("/admin/login")) {
    config.params = { ...(config.params || {}), club_id: clubId };
  }
  return config;
});

export const api = {
  // clubs
  listClubs: () => axios.get(`${API}/clubs`).then((r) => r.data),
  patchClub: (id, patch) => axios.patch(`${API}/clubs/${id}`, patch, { headers: adminHeaders() }).then((r) => r.data),
  // players
  listPlayers: () => axios.get(`${API}/players`).then((r) => r.data),
  createPlayer: (p) => axios.post(`${API}/players`, p, { headers: adminHeaders() }).then((r) => r.data),
  patchPlayer: (id, patch) => axios.patch(`${API}/players/${id}`, patch, { headers: adminHeaders() }).then((r) => r.data),
  deletePlayer: (id) => axios.delete(`${API}/players/${id}`, { headers: adminHeaders() }).then((r) => r.data),

  // seasons
  listSeasons: () => axios.get(`${API}/seasons`).then((r) => r.data),
  activeSeason: () => axios.get(`${API}/seasons/active`).then((r) => r.data),
  createSeason: (s) => axios.post(`${API}/seasons`, s, { headers: adminHeaders() }).then((r) => r.data),
  patchSeason: (id, patch) => axios.patch(`${API}/seasons/${id}`, patch, { headers: adminHeaders() }).then((r) => r.data),
  activateSeason: (id) => axios.post(`${API}/seasons/${id}/activate`, {}, { headers: adminHeaders() }).then((r) => r.data),
  archiveSeason: (id) => axios.post(`${API}/seasons/${id}/archive`, {}, { headers: adminHeaders() }).then((r) => r.data),
  restoreSeason: (id) => axios.post(`${API}/seasons/${id}/restore`, {}, { headers: adminHeaders() }).then((r) => r.data),
  deleteSeason: (id) => axios.delete(`${API}/seasons/${id}`, { headers: adminHeaders() }).then((r) => r.data),
  emailSeasonProfiles: (id) => axios.post(`${API}/seasons/${id}/email-profiles`, {}, { headers: adminHeaders() }).then((r) => r.data),

  // matchdays
  listMatchdays: (sid) => axios.get(`${API}/seasons/${sid}/matchdays`).then((r) => r.data),
  addMatchday: (sid) => axios.post(`${API}/seasons/${sid}/matchdays`, {}, { headers: adminHeaders() }).then((r) => r.data),
  getMatchday: (id) => axios.get(`${API}/matchdays/${id}`).then((r) => r.data),
  matchdayReport: (id) => axios.get(`${API}/matchdays/${id}/report`).then((r) => r.data),
  emailMatchdayReport: (id) => axios.post(`${API}/matchdays/${id}/email-report`, {}, { headers: adminHeaders() }).then((r) => r.data),
  patchMatchday: (id, patch) => axios.patch(`${API}/matchdays/${id}`, patch, { headers: adminHeaders() }).then((r) => r.data),
  generateMatchday: (id, options = {}) => axios.post(`${API}/matchdays/${id}/generate`, options, { headers: adminHeaders() }).then((r) => r.data),
  clearMatchday: (id) => axios.delete(`${API}/matchdays/${id}/matches`, { headers: adminHeaders() }).then((r) => r.data),

  // matches
  listMatches: (params = {}) => axios.get(`${API}/matches`, { params }).then((r) => r.data),
  getMatch: (id) => axios.get(`${API}/matches/${id}`).then((r) => r.data),
  submitResult: (id, legs, extra = {}) => axios.post(`${API}/matches/${id}/result`, { legs, ...extra }, { headers: adminHeaders() }).then((r) => r.data),
  submitHistoricResult: (id, result) => axios.post(`${API}/matches/${id}/historic-result`, result, { headers: adminHeaders() }).then((r) => r.data),
  resetMatch: (id) => axios.post(`${API}/matches/${id}/reset`, {}, { headers: adminHeaders() }).then((r) => r.data),

  // tables
  tableMatchday: (mid) => axios.get(`${API}/table/matchday/${mid}`).then((r) => r.data),
  tableSeason: (sid) => axios.get(`${API}/table/season/${sid}`).then((r) => r.data),
  tableEternal: () => axios.get(`${API}/table/eternal`).then((r) => r.data),

  // stats
  leaderboardsSeason: (sid) => axios.get(`${API}/stats/leaderboards/season/${sid}`).then((r) => r.data),
  leaderboardsAll: () => axios.get(`${API}/stats/leaderboards/all`).then((r) => r.data),
  playerStats: (pid, seasonId) => axios.get(`${API}/stats/player/${pid}`, { params: seasonId ? { season_id: seasonId } : {} }).then((r) => r.data),
  h2h: (p1, p2, seasonId) => axios.get(`${API}/stats/h2h/${p1}/${p2}`, { params: seasonId ? { season_id: seasonId } : {} }).then((r) => r.data),
  career: () => axios.get(`${API}/stats/career`).then((r) => r.data),
  matchReport: (id) => axios.get(`${API}/matches/${id}/report`).then((r) => r.data),

  // historic
  listHistoric: (sid) => axios.get(`${API}/seasons/${sid}/historic`).then((r) => r.data),
  upsertHistoric: (sid, entries) => axios.post(`${API}/seasons/${sid}/historic`, entries, { headers: adminHeaders() }).then((r) => r.data),
  deleteHistoric: (eid) => axios.delete(`${API}/historic/${eid}`, { headers: adminHeaders() }).then((r) => r.data),
  clearHistoric: (sid) => axios.delete(`${API}/seasons/${sid}/historic`, { headers: adminHeaders() }).then((r) => r.data),

  // dashboard extras
  dashboardExtra: (sid) => axios.get(`${API}/dashboard/extra/${sid}`).then((r) => r.data),

  // admin
  adminLogin: (password) => axios.post(`${API}/admin/login`, { password }).then((r) => r.data),
  adminReset: () => axios.post(`${API}/admin/reset`, {}, { headers: adminHeaders() }).then((r) => r.data),

  // backup
  exportBackup: () => axios.get(`${API}/backup/export`, { headers: adminHeaders() }).then((r) => r.data),
  importBackup: (data) => axios.post(`${API}/backup/import`, data, { headers: adminHeaders() }).then((r) => r.data),
};
